const DEFAULT_ITEMS_PER_PAGE = 10;
const DEFAULT_PAGINATION_OPTIONS = [10, 20, 50, 100];
const ORDER_DIRECTIONS = {
  ASC: "ASC",
  DESC: "DESC",
};

// In-memory cache so pagination state survives navigating away from a table
// (e.g. opening an order's details) and back, within the same SPA session.
// Keyed by a component-supplied `paginationContextKey`.
const paginationStateCache = Object.create(null);

export default {
  props: {
    startDate: {
      type: String,
      default: "",
    },
    endDate: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      queryRef: "",
      singleSelect: false,
      selectedRows: [],
      orderBy: {},
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      nextCursor: null,
      prevCursor: null,
      totalCount: 0,
      pageData: {},
      currentPage: 1,
      defaultPaginationOptions: DEFAULT_PAGINATION_OPTIONS,
      restoringPagination: false,
    };
  },
  created() {
    this.restorePaginationState();
  },
  watch: {
    itemsPerPage() {
      // Skip when the change comes from restoring cached state (cursors/page are
      // already consistent with the restored size).
      if (this.restoringPagination) return;
      // A page-size change invalidates cursors captured at the previous size,
      // so reset to the first page before caching the new size.
      this.nextCursor = null;
      this.prevCursor = null;
      this.currentPage = 1;
      this.persistPaginationState();
    },
    paginationContextKey(newKey, oldKey) {
      if (!newKey || newKey === oldKey) return;

      // Reset pagination when context changes (filters/entity) to avoid stale cursors.
      this.currentPage = 1;
      this.nextCursor = null;
      this.prevCursor = null;

      this.restorePaginationState();
      this.persistPaginationState();
    },
  },
  computed: {
    // Return a stable, filter-aware string to enable pagination persistence.
    // Null (default) disables persistence so existing tables are unaffected.
    paginationContextKey() {
      return null;
    },
    filterDates() {
      let dates = {};
      if (this.startDate) {
        dates.startDate = new Date(this.startDate.replaceAll("-", "/"));
      }
      if (this.endDate) {
        dates.endDate = new Date(this.endDate.replaceAll("-", "/"));
        dates.endDate.setDate(dates.endDate.getDate() + 1);
      }
      return dates;
    },
  },
  methods: {
    formatDate(date) {
      let publicationDate = new Date(date);
      var options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
      };
      return publicationDate.toLocaleDateString("en-us", options);
    },
    updateSort(byDesc, event) {
      this.orderBy = {};
      if (byDesc == "by") {
        this.sortBy = null;
        if (event != null) {
          let orderByString = event.replace(".", "_");
          this.sortBy = orderByString;
          this.orderBy[this.sortBy] = this.sortDescending(false);
        }
      } else if (byDesc == "desc") {
        if (event != null) {
          this.orderBy[this.sortBy] = this.sortDescending(event);
        }
      }
      // Cursors are tied to a specific ordering; changing the sort invalidates
      // them, so reset to the first page and persist the new state.
      this.nextCursor = null;
      this.prevCursor = null;
      this.currentPage = 1;
      this.persistPaginationState();
    },
    customSort(value) {
      return value;
    },
    sortDescending(isDescending) {
      return isDescending ? ORDER_DIRECTIONS.DESC : ORDER_DIRECTIONS.ASC;
    },
    updateData(data, entityName) {
      this.totalCount = data[entityName].totalCount;
      this.pageData = data[entityName].pageInfo;
      return data[entityName].edges.map((edge) => edge.node);
    },
    setCursor(newCursor, isNext) {
      if (isNext > this.currentPage && this.nextCursor != newCursor.endCursor) {
        this.currentPage++;
        this.nextCursor = newCursor.endCursor;
        this.prevCursor = null;
      } else if (
        isNext < this.currentPage &&
        this.prevCursor != newCursor.startCursor
      ) {
        this.currentPage--;
        this.nextCursor = null;
        this.prevCursor = newCursor.startCursor;
      }
      this.persistPaginationState();
    },
    persistPaginationState() {
      if (!this.paginationContextKey) return;
      paginationStateCache[this.paginationContextKey] = {
        itemsPerPage: this.itemsPerPage,
        currentPage: this.currentPage,
        nextCursor: this.nextCursor,
        prevCursor: this.prevCursor,
      };
    },
    restorePaginationState() {
      if (!this.paginationContextKey) return;
      const saved = paginationStateCache[this.paginationContextKey];
      if (!saved) return;
      // Guard the itemsPerPage watcher so restoring a cached size does not
      // wipe the cached cursors/page it belongs with.
      this.restoringPagination = true;
      this.itemsPerPage = saved.itemsPerPage;
      this.currentPage = saved.currentPage;
      this.nextCursor = saved.nextCursor;
      this.prevCursor = saved.prevCursor;
      this.$nextTick(() => {
        this.restoringPagination = false;
      });
    },
    refetchQuery(queryRef) {
      this.$refs[queryRef].$apollo.queries.query.refetch();
    },
  },
}